import { useState } from "react";

import { usePostHog } from "posthog-js/react";
import { useLocation } from "react-router-dom";
import { useSWRConfig } from "swr";

import { extractAPIRequestErrorMessage } from "utils";

import { useAPI } from "./useAPI";
import { useError } from "./useError";
import { useLoading } from "./useLoading";
import { ClientAppEvent } from "../constants";

interface EditCompanyProfilePayload {
  description: string;
  business_type: BusinessTypeValue;
  number_of_trucks: number | null;
  number_of_trailers: number | null;
  equipment_types: string;
  service_areas: string;
  phone_number: string | null;
  email: string | null;
  website_url: string | null;
  facebook_url: string | null;
  x_url: string | null;
  linkedin_url: string | null;
  instagram_url: string | null;
}

interface Props {
  companyId: number;
  onEditSuccess?: VoidFunction;
  onEditFailure?: (e: Error) => void;
}

export function useEditCompanyProfile(props: Props) {
  const { companyId, onEditSuccess, onEditFailure } = props;
  const { api } = useAPI();
  const { error, setError } = useError();
  const { loading, setLoading } = useLoading(false);
  const [success, setSuccess] = useState<boolean>(false);
  const posthog = usePostHog();
  const location = useLocation();
  const { mutate } = useSWRConfig();

  const resetState = () => {
    setError(null);
    setLoading(false);
    setSuccess(false);
  };

  function editProfile(payload: EditCompanyProfilePayload) {
    setError(null);
    setLoading(true);
    api("post", `/companies/${companyId}/carrier-about`, payload)
      .then(() => {
        setSuccess(true);
        setError(null);
        posthog?.capture(ClientAppEvent.CARRIER_COMPANY_PROFILE_UPDATED, {
          ...payload,
          path: location.pathname,
        });
        mutate(`/companies/${companyId}/carrier-profile`);
        if (onEditSuccess) {
          onEditSuccess();
        }
      })
      .catch((e) => {
        setError(
          extractAPIRequestErrorMessage(e, "Failed to update company profile"),
        );
        setSuccess(false);
        if (onEditFailure) {
          onEditFailure(e);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return {
    loading,
    error,
    success,
    editProfile,
    resetState,
  };
}
